import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { formatArea } from '../lib/formatters.js';
import { fetchListingCategories, searchPublishedListings } from './api.js';
import { PortalListingAttrFilters } from '../components/PortalListingAttrFilters.jsx';
import { VitrinCategorySidebar } from './VitrinCategorySidebar.jsx';
import { VitrinLocationSelects } from './VitrinLocationSelects.jsx';

const CATEGORY_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'arazi', label: 'Arazi' },
  { key: 'imarli_arsa', label: 'İmarlı arsa' },
  { key: 'yapi', label: 'Yapı' },
  { key: 'ticari', label: 'Ticari' },
];

const LISTING_TYPES = [
  { value: '', label: 'İlan tipi' },
  { value: 'sale', label: 'Satılık' },
  { value: 'rent', label: 'Kiralık' },
  { value: 'daily', label: 'Günlük' },
];

/** media/vitrin/<dosya> — ana kategori sekmesi anahtarına göre (Tümü dahil) */
const VITRIN_HERO_BG_BY_MAIN = {
  '': 'tumu.png',
  imarli_arsa: 'arsa.png',
  arazi: 'tarla.png',
  ticari: 'ticari.png',
  yapi: 'villa.png',
};

function vitrinHeroBgUrl(categoryMain) {
  const key = categoryMain ?? '';
  const file = VITRIN_HERO_BG_BY_MAIN[key];
  if (!file) return null;
  return `/media/vitrin/${file}`;
}

function formatPrice(n) {
  if (n == null || n === '') return '—';
  const x = Number(n);
  if (Number.isNaN(x)) return String(n);
  return `${x.toLocaleString('tr-TR')} TL`;
}

function consultantStarInfo(scoreRaw) {
  const score = Math.max(0, Number(scoreRaw) || 0);
  const outOfFive = Math.max(0, Math.min(5, score / 20));
  const filledStars = Math.max(0, Math.min(5, Math.round(outOfFive)));
  return { score, outOfFive, filledStars };
}

export function VitrinApp() {
  const [categoryMain, setCategoryMain] = useState('');
  const [categoryLeafId, setCategoryLeafId] = useState('');
  const [categoryTypeMid, setCategoryTypeMid] = useState('');
  const [listingType, setListingType] = useState('');
  const [cityId, setCityId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [quarterId, setQuarterId] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [listingAttr, setListingAttr] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 24;

  const [subOptions, setSubOptions] = useState([]);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [topConsultants, setTopConsultants] = useState([]);
  const [topConsultantsLoading, setTopConsultantsLoading] = useState(true);
  const [topConsultantsError, setTopConsultantsError] = useState('');
  /** Sayfa N için cursor (API: page=1 + cursor_*); ardışık sayfalama için */
  const listingCursorsRef = useRef({});

  useEffect(() => {
    listingCursorsRef.current = {};
  }, [
    categoryMain,
    categoryLeafId,
    categoryTypeMid,
    listingType,
    cityId,
    districtId,
    quarterId,
    priceMin,
    priceMax,
    listingAttr,
  ]);

  useEffect(() => {
    if (!categoryMain) {
      setSubOptions([]);
      return;
    }
    let cancelled = false;
    fetchListingCategories(categoryMain)
      .then((d) => {
        if (!cancelled) setSubOptions(d?.nodes || []);
      })
      .catch(() => {
        if (!cancelled) setSubOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [categoryMain]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = { page_size: String(pageSize) };
      const cursor = listingCursorsRef.current[page];
      if (page > 1 && cursor?.updated_at && cursor?.listing_id) {
        params.page = '1';
        params.cursor_updated_at = cursor.updated_at;
        params.cursor_listing_id = cursor.listing_id;
      } else {
        params.page = String(page);
      }
      if (categoryMain) params.category_main = categoryMain;
      if (categoryLeafId) params.category_leaf_id = categoryLeafId;
      else if (categoryTypeMid) params.category_type = categoryTypeMid;
      if (listingType) params.listing_type = listingType;
      if (cityId.trim()) params.city_id = cityId.trim();
      if (districtId.trim()) params.district_id = districtId.trim();
      if (quarterId.trim()) params.quarter_id = quarterId.trim();
      if (priceMin.trim()) params.price_min_amount = priceMin.trim();
      if (priceMax.trim()) params.price_max_amount = priceMax.trim();
      if (String(listingAttr).trim()) params.listing_attr = String(listingAttr).trim();
      const { items: next, pagination } = await searchPublishedListings(params);
      setItems(next);
      setTotalCount(pagination.total_count ?? next.length ?? 0);
      if (pagination.next_cursor?.updated_at && pagination.next_cursor?.listing_id) {
        listingCursorsRef.current[page + 1] = pagination.next_cursor;
      } else {
        delete listingCursorsRef.current[page + 1];
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    categoryMain,
    categoryLeafId,
    categoryTypeMid,
    listingType,
    cityId,
    districtId,
    quarterId,
    priceMin,
    priceMax,
    listingAttr,
  ]);

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  useEffect(() => {
    let cancelled = false;
    setTopConsultantsLoading(true);
    setTopConsultantsError('');
    fetch('/accounts/emlak-danismanlari/api/?page=1&page_size=5', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.error || data?.message || 'Danışmanlar yüklenemedi.');
        }
        return Array.isArray(data?.results) ? data.results : [];
      })
      .then((rows) => {
        if (!cancelled) {
          setTopConsultants(rows);
        }
      })
      .catch((requestError) => {
        if (!cancelled) {
          setTopConsultants([]);
          setTopConsultantsError(requestError instanceof Error ? requestError.message : String(requestError));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTopConsultantsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Son 30 Gün / portal liste sayfası — vitrin filtreleriyle aynı konum ve fiyat aralığı */
  const portalRecentQueriesListHref = useMemo(() => {
    const q = new URLSearchParams();
    q.set('ref', 'vitrin');
    q.set('list_mode', 'listings');
    q.set('sort_dir', 'desc');
    if (cityId) q.set('city_id', String(cityId));
    if (districtId) q.set('town_id', String(districtId));
    if (quarterId) q.set('quarter_id', String(quarterId));
    if (priceMin.trim()) q.set('total_price_min', priceMin.trim());
    if (priceMax.trim()) q.set('total_price_max', priceMax.trim());
    return `/portal/recent-queries/?${q.toString()}`;
  }, [cityId, districtId, quarterId, priceMin, priceMax]);

  const onSubmitFilters = (e) => {
    e.preventDefault();
    window.location.assign(portalRecentQueriesListHref);
  };

  const clearCategoryFilters = () => {
    setCategoryLeafId('');
    setCategoryTypeMid('');
    setListingAttr('');
  };

  const onSelectMain = useCallback((mainId) => {
    setCategoryMain(mainId);
    setCategoryLeafId('');
    setCategoryTypeMid('');
    setListingAttr('');
    setPage(1);
  }, []);

  const onSelectLeaf = useCallback((leafId, rootId) => {
    setCategoryMain(rootId);
    setCategoryLeafId(leafId);
    setCategoryTypeMid('');
    setListingAttr('');
    setPage(1);
  }, []);

  const onSelectBranch = useCallback((branchId, rootId) => {
    setCategoryMain(rootId);
    setCategoryLeafId('');
    setCategoryTypeMid(branchId);
    setListingAttr('');
    setPage(1);
  }, []);

  const onLocationChange = useCallback(({ cityId: c, districtId: d, quarterId: q }) => {
    setCityId(c || '');
    setDistrictId(d || '');
    setQuarterId(q || '');
    setPage(1);
  }, []);

  const subSelectValue = categoryLeafId
    ? `leaf:${categoryLeafId}`
    : categoryTypeMid
      ? `branch:${categoryTypeMid}`
      : '';

  const listingHref = (row) => {
    const rt =
      typeof window !== 'undefined'
        ? encodeURIComponent(`${window.location.pathname}${window.location.search || ''}`)
        : '';
    const sid = row?.portal_snapshot_id ?? row?.portalSnapshotId;
    if (sid != null && sid !== '') {
      return `/portal/recent-queries/${encodeURIComponent(String(sid))}/?returnTo=${rt}`;
    }
    if (row?.listing_id) {
      return `/portal/recent-queries/listing/${encodeURIComponent(String(row.listing_id))}/?returnTo=${rt}`;
    }
    return `/portal/recent-queries/?returnTo=${rt}`;
  };

  const heroBgUrl = useMemo(() => vitrinHeroBgUrl(categoryMain), [categoryMain]);
  const heroInnerStyle = useMemo(() => {
    if (!heroBgUrl) return undefined;
    return { backgroundImage: `url("${heroBgUrl}")` };
  }, [heroBgUrl]);

  return (
    <div class="pp-vitrin pp-vitrin--layout">
      <div class="pp-vitrin__sidebar-col">
        <VitrinCategorySidebar
          activeMainId={categoryMain}
          activeLeafId={categoryLeafId}
          activeBranchId={categoryTypeMid}
          onSelectMain={onSelectMain}
          onSelectLeaf={onSelectLeaf}
          onSelectBranch={onSelectBranch}
        />
        <div class="pp-vitrin__attr-filters">
          <PortalListingAttrFilters
            categoryMain={categoryMain}
            categoryTypeCsv={categoryTypeMid}
            categoryLeafIdCsv={categoryLeafId}
            listMode="listings"
            listingAttrJson={listingAttr}
            onListingAttrChange={(json) => {
              setListingAttr(json);
              setPage(1);
            }}
          />
        </div>
        <aside class="pp-vitrin-sidebar-ad" aria-label="Reklam">
          <div class="pp-vitrin-sidebar-ad__slot">
            <img
              class="pp-vitrin-sidebar-ad__img"
              src="/media/vitrin/reklam_ne_ekilir.png"
              alt="Reklam"
              width={300}
              height={250}
              loading="lazy"
              decoding="async"
            />
          </div>
        </aside>
        <aside class="pp-vitrin-sidebar-consultants" aria-label="Öne çıkan danışmanlar">
          <div class="pp-vitrin-sidebar-consultants__head">
            <strong>Öne çıkan danışmanlar</strong>
            <span>Ülke çapında yüksek puanlı ilk 5 danışman</span>
          </div>
          {topConsultantsError ? (
            <div class="pp-vitrin-sidebar-consultants__empty">{topConsultantsError}</div>
          ) : topConsultantsLoading ? (
            <div class="pp-vitrin-sidebar-consultants__empty">Danışmanlar yükleniyor...</div>
          ) : topConsultants.length ? (
            <div class="pp-vitrin-sidebar-consultants__list">
              {topConsultants.map((consultant) => {
                const profileUrl = String(consultant?.badges_url || '').trim() || '/accounts/emlak-danismanlari/';
                const title = String(consultant?.company_name || consultant?.full_name || 'Danışman').trim();
                const subtitle = consultant?.company_name && consultant?.full_name && consultant.full_name !== consultant.company_name
                  ? consultant.full_name
                  : '';
                const starInfo = consultantStarInfo(consultant?.expert_score_current);
                return (
                  <a key={consultant.user_id || title} class="pp-vitrin-sidebar-consultants__item" href={profileUrl}>
                    {consultant?.avatar_url ? (
                      <img class="pp-vitrin-sidebar-consultants__avatar" src={consultant.avatar_url} alt={title} loading="lazy" />
                    ) : (
                      <div class="pp-vitrin-sidebar-consultants__avatar is-empty" aria-hidden="true">
                        <i class="fas fa-user-tie" />
                      </div>
                    )}
                    <div class="pp-vitrin-sidebar-consultants__copy">
                      <strong>{title}</strong>
                      {subtitle ? <span>{subtitle}</span> : null}
                      <div class="pp-vitrin-sidebar-consultants__stars" aria-label={`Danışman puanı ${starInfo.outOfFive.toFixed(1)} / 5`}>
                        {Array.from({ length: 5 }).map((_, index) => (
                          <i
                            key={`${consultant.user_id || title}-star-${index}`}
                            class={`fas fa-star${index < starInfo.filledStars ? ' is-on' : ''}`}
                            aria-hidden="true"
                          />
                        ))}
                        <em>{starInfo.outOfFive.toFixed(1)}</em>
                      </div>
                      <small>
                        {consultant.member_label || 'Danışman'} · Puan {Number(consultant.expert_score_current || 0).toLocaleString('tr-TR')}
                      </small>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <div class="pp-vitrin-sidebar-consultants__empty">Danışman bulunamadı.</div>
          )}
        </aside>
      </div>

      <div class="pp-vitrin__main-col">
        <section class="pp-vitrin__hero" aria-label="Filtreler">
          <div
            class={`pp-vitrin__hero-inner${heroBgUrl ? ' pp-vitrin__hero-inner--bg' : ''}`}
            style={heroInnerStyle}
          >
            <div class="pp-vitrin__hero-panel">
            <p class="pp-vitrin__eyebrow">İlan portali</p>
            <h1 class="pp-vitrin__title">Emlak Vitrini</h1>
            <div class="pp-vitrin__tabs" role="tablist" aria-label="Ana kategori (hızlı)">
              {CATEGORY_TABS.map((t) => (
                <button
                  key={t.key || 'all'}
                  type="button"
                  role="tab"
                  class={`pp-vitrin__tab${categoryMain === t.key ? ' is-active' : ''}`}
                  aria-selected={categoryMain === t.key}
                  onClick={() => {
                    setCategoryMain(t.key);
                    clearCategoryFilters();
                    setPage(1);
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <form class="pp-vitrin__filters" onSubmit={onSubmitFilters}>
              <div class="pp-vitrin__filter-cell">
                <select
                  aria-label="İlan tipi"
                  value={listingType}
                  onChange={(e) => {
                    setListingType(e.target.value);
                    setPage(1);
                  }}
                >
                  {LISTING_TYPES.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              {categoryMain ? (
                <div class="pp-vitrin__filter-cell">
                  <select
                    aria-label="Kategori"
                    value={subSelectValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        setCategoryLeafId('');
                        setCategoryTypeMid('');
                      } else if (v.startsWith('leaf:')) {
                        setCategoryLeafId(v.slice(5));
                        setCategoryTypeMid('');
                      } else if (v.startsWith('branch:')) {
                        setCategoryTypeMid(v.slice(7));
                        setCategoryLeafId('');
                      }
                      setPage(1);
                    }}
                  >
                    <option value="">Kategori</option>
                    {subOptions.map((n) => (
                      <option
                        key={n.id}
                        value={n.is_leaf ? `leaf:${n.id}` : `branch:${n.id}`}
                      >
                        {n.label}
                        {n.is_leaf ? '' : ' ›'}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <VitrinLocationSelects
                cityId={cityId}
                districtId={districtId}
                quarterId={quarterId}
                onLocationChange={onLocationChange}
              />
              <div class="pp-vitrin__filter-cell">
                <input
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Min fiyat (TL)"
                  aria-label="Min fiyat (TL)"
                  value={priceMin}
                  onInput={(e) => setPriceMin(e.target.value)}
                />
              </div>
              <div class="pp-vitrin__filter-cell">
                <input
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Max fiyat (TL)"
                  aria-label="Max fiyat (TL)"
                  value={priceMax}
                  onInput={(e) => setPriceMax(e.target.value)}
                />
              </div>
            </form>
            <div class="pp-vitrin__actions">
              <a
                class="pp-vitrin__btn pp-vitrin__btn--primary"
                href={portalRecentQueriesListHref}
              >
                Listele
              </a>
              <a class="pp-vitrin__btn pp-vitrin__btn--ghost" href="/portal/ilan/olustur/">
                İlan ver
              </a>
            </div>
            </div>
          </div>
        </section>

        <div class="pp-vitrin__head-row">
          <h2>Vitrin ilanları</h2>
          <a class="pp-vitrin__link-all" href="/portal/ilan/vitrin/">
            Tüm vitrin ilanlarını göster
          </a>
        </div>
        {totalCount > 0 ? (
          <p class="pp-vitrin__card-meta" style={{ margin: '0 0 0.75rem', padding: '0 0.1rem' }}>
            {totalCount.toLocaleString('tr-TR')} ilan · sayfa {page}
          </p>
        ) : null}

        {err ? <div class="pp-vitrin__err">{err}</div> : null}

        {!loading && !err && items.length === 0 ? (
          <div class="pp-vitrin__empty">
            Yayınlanmış ilan bulunamadı. Filtreleri gevşetin veya başka kategori deneyin.
          </div>
        ) : null}

        <div class="pp-vitrin__grid">
          {items.map((row) => (
            <a key={row.listing_id} class="pp-vitrin__card" href={listingHref(row)}>
              <div class="pp-vitrin__card-img-wrap">
                {row.thumb_url ? (
                  <img class="pp-vitrin__card-img" src={row.thumb_url} alt="" loading="lazy" />
                ) : (
                  <div
                    class="pp-vitrin__card-img"
                    style={{ background: 'linear-gradient(145deg, #cbd5e1, #94a3b8)' }}
                    aria-hidden
                  />
                )}
              </div>
              <div class="pp-vitrin__card-body">
                <p
                  class="pp-vitrin__card-title"
                  title={row.title?.trim() || 'Başlıksız ilan'}
                >
                  {row.title?.trim() || 'Başlıksız ilan'}
                </p>
                {row.category_leaf_label ? (
                  <p class="pp-vitrin__card-category">{row.category_leaf_label}</p>
                ) : null}
                <p class="pp-vitrin__card-meta">
                  {formatPrice(row.price_amount)}
                  {row.area_m2 != null && row.area_m2 !== '' ? ` · ${formatArea(row.area_m2)} m²` : ''}
                </p>
              </div>
            </a>
          ))}
        </div>

        {totalCount > pageSize ? (
          <div
            style={{
              marginTop: '1rem',
              display: 'flex',
              gap: '0.5rem',
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              class="pp-vitrin__btn pp-vitrin__btn--ghost"
              disabled={loading || page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Önceki
            </button>
            <button
              type="button"
              class="pp-vitrin__btn pp-vitrin__btn--ghost"
              disabled={loading || page * pageSize >= totalCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Sonraki
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
